import { v4 as uuid } from 'uuid'
import { cloneDeep } from 'lodash-es'
import type { UnwrapRef } from 'vue'

import { componentMeta } from '@canvas-builder/constants'
import { simpleDeepClone } from '@canvas-builder/util/clone'
import type { SectionOverrides } from '@canvas-builder/types/canvas'
import {
  ComponentTypes,
  type CarouselProps,
  type ComponentMeta,
  type ComponentNode,
  type ContentGridProps,
  type EmbeddableProps,
  type GridItemProps,
  type GridProps,
  type HtmlProps,
  type IFrameProps,
  type MultimediaProps,
  type SectionComponentOverrides,
  type SectionListProps,
  type TextProps,
  type CanvasBuilderMode,
  type ComponentNodeVisible,
  type LinkProps,
  type BlockProps,
} from '@canvas-builder/types/canvas'
import { findNodeInTreeById } from '@canvas-builder/util/tree'
import { stripHtmlTags, stripHtmlTagsAndTrim, sanitizeHtml } from '@lib/util/string'
import type { CanvasRetrieve, FileRetrieve } from '@openapi'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import useCanvasThemeAssets from '@canvas-builder/composables/useCanvasThemeAssets'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import { type EmbedLocation } from '@lib/types/app'
import { renderTemplate } from '@lib/util/handlebars'
import { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { type SectionComponentNodesBySectionId, type ComponentNodesById } from '@canvas-builder/composables/useCanvas'
import { prepareContentGridNodeData } from '@canvas-builder/composables/useContentSelector'
import { type SelectedPage, type SelectedFile } from '@content-selector/stores/app'
import type {
  CollectionPlayer,
  CollectionPlayerV2,
  CollectionGroupV2,
} from '@collection-player/types/collection-player.type'
import { parseContentSelectorToCollectionPlayerSlides } from '@collection-selector/utils/content-selector-adapter.util'

export function createNodeId(type?: ComponentTypes): string {
  return `${type || 'UnknownType'}-${uuid()}`
}

export function getComponentKeywords(type: ComponentTypes, t: (input: string) => string): string[] {
  switch (type) {
    case ComponentTypes.Carousel:
      return [
        t('canvasUI.canvasBuilder.keywords.slide'),
        t('canvasUI.canvasBuilder.keywords.image'),
        t('canvasUI.canvasBuilder.keywords.slider'),
      ]
    case ComponentTypes.Grid:
      return [t('canvasUI.canvasBuilder.keywords.block')]
    case ComponentTypes.Html:
      return [
        t('canvasUI.canvasBuilder.keywords.code'),
        t('canvasUI.canvasBuilder.keywords.widget'),
        t('canvasUI.canvasBuilder.keywords.embed'),
        t('canvasUI.canvasBuilder.keywords.html'),
      ]
    case ComponentTypes.IFrame:
      return [
        t('canvasUI.canvasBuilder.keywords.widget'),
        t('canvasUI.canvasBuilder.keywords.embed'),
        t('canvasUI.canvasBuilder.keywords.url'),
      ]
    case ComponentTypes.Text:
      return [
        t('canvasUI.canvasBuilder.keywords.text'),
        t('canvasUI.canvasBuilder.keywords.content'),
        t('canvasUI.canvasBuilder.keywords.paragraph'),
      ]
    case ComponentTypes.Multimedia:
      return [
        t('canvasUI.canvasBuilder.keywords.image'),
        t('canvasUI.canvasBuilder.keywords.video'),
        t('canvasUI.canvasBuilder.keywords.photo'),
        t('canvasUI.canvasBuilder.keywords.clip'),
        t('canvasUI.canvasBuilder.keywords.multimedia'),
        t('canvasUI.canvasBuilder.keywords.url'),
      ]
    case ComponentTypes.ContentGrid:
      return [
        t('canvasUI.canvasBuilder.keywords.pdf'),
        t('canvasUI.canvasBuilder.keywords.slide'),
        t('canvasUI.canvasBuilder.keywords.image'),
        t('canvasUI.canvasBuilder.keywords.ar'),
        t('canvasUI.canvasBuilder.keywords.video'),
        t('canvasUI.canvasBuilder.keywords.document'),
        t('canvasUI.canvasBuilder.keywords.content'),
      ]
    case ComponentTypes.Link:
      return [
        t('canvasUI.canvasBuilder.keywords.link'),
        t('canvasUI.canvasBuilder.keywords.url'),
        t('canvasUI.canvasBuilder.keywords.button'),
      ]
    case ComponentTypes.Block:
      return [
        t('canvasUI.canvasBuilder.keywords.block'),
        t('canvasUI.canvasBuilder.keywords.group'),
        t('canvasUI.canvasBuilder.keywords.widget'),
      ]
    default:
      return []
  }
}

export function getComponentTitle(
  type: ComponentTypes | string,
  t: (input: string) => string,
  options: { name?: string } = {},
): string {
  switch (type) {
    case ComponentTypes.Root:
      return t('canvasUI.canvasBuilder.components.titles.canvas')
    case ComponentTypes.Carousel:
      return t('canvasUI.canvasBuilder.components.titles.carousel')
    case ComponentTypes.Embeddable:
      return t('canvasUI.canvasBuilder.components.titles.embeddable')
    case ComponentTypes.Grid:
      return t('canvasUI.canvasBuilder.components.titles.grid')
    case ComponentTypes.GridItem:
      return t('canvasUI.canvasBuilder.components.titles.gridItem')
    case ComponentTypes.Html:
      return t('canvasUI.canvasBuilder.components.titles.html')
    case ComponentTypes.IFrame:
      return t('canvasUI.canvasBuilder.components.titles.iFrame')
    case ComponentTypes.Link:
      return t('canvasUI.canvasBuilder.components.titles.link')
    case ComponentTypes.Text:
      return t('canvasUI.canvasBuilder.components.titles.text')
    case ComponentTypes.Multimedia:
      return t('canvasUI.canvasBuilder.components.titles.multimedia')
    case ComponentTypes.ContentGrid:
      return t('canvasUI.canvasBuilder.components.titles.contentGrid')
    case ComponentTypes.SectionList:
      return t('canvasUI.canvasBuilder.components.titles.sectionList')
    case ComponentTypes.ContentList:
      return t('canvasUI.canvasBuilder.components.titles.contentList')
    case ComponentTypes.PageBreak:
      return t('canvasUI.canvasBuilder.components.titles.pageBreak')
    case ComponentTypes.Page:
      return t('canvasUI.canvasBuilder.components.titles.page')
    case ComponentTypes.Timeline:
      return t('canvasUI.canvasBuilder.components.titles.timeline')
    case ComponentTypes.CollectionPlayer:
      return t('canvasUI.canvasBuilder.components.titles.collectionPlayer')
    case ComponentTypes.Block:
      return options.name || t('canvasUI.canvasBuilder.components.titles.block')
    case ComponentTypes.Scribble:
      return options.name || t('canvasUI.canvasBuilder.components.titles.scribble')
    case ComponentTypes.DynamicDataTable:
      return options.name || t('canvasUI.canvasBuilder.components.titles.dynamicDataTable')
    case ComponentTypes.DataCharts:
      return t('canvasUI.canvasBuilder.components.titles.dataCharts')
    case ComponentTypes.CustomDataTable:
      return t('canvasUI.canvasBuilder.components.titles.customDataTable')
    default:
      return ''
  }
}

export function getComponentDescription(
  type: ComponentTypes | string,
  t: (input: string) => string,
  options: { description?: string } = {},
): string {
  switch (type) {
    case ComponentTypes.Embeddable:
      return t('canvasUI.canvasBuilder.components.descriptions.embeddable')
    case ComponentTypes.Grid:
      return t('canvasUI.canvasBuilder.components.descriptions.grid')
    case ComponentTypes.Text:
      return t('canvasUI.canvasBuilder.components.descriptions.text')
    case ComponentTypes.Multimedia:
      return t('canvasUI.canvasBuilder.components.descriptions.multimedia')
    case ComponentTypes.ContentGrid:
      return t('canvasUI.canvasBuilder.components.descriptions.contentGrid')
    case ComponentTypes.SectionList:
      return t('canvasUI.canvasBuilder.components.descriptions.sectionList')
    case ComponentTypes.Html:
      return t('canvasUI.canvasBuilder.components.descriptions.html')
    case ComponentTypes.IFrame:
      return t('canvasUI.canvasBuilder.components.descriptions.iFrame')
    case ComponentTypes.Carousel:
      return t('canvasUI.canvasBuilder.components.descriptions.carousel')
    case ComponentTypes.Link:
      return t('canvasUI.canvasBuilder.components.descriptions.link')
    case ComponentTypes.PageBreak:
      return t('canvasUI.canvasBuilder.components.descriptions.pageBreak')
    case ComponentTypes.Page:
      return t('canvasUI.canvasBuilder.components.descriptions.page')
    case ComponentTypes.Timeline:
      return t('canvasUI.canvasBuilder.components.descriptions.timeline')
    case ComponentTypes.CollectionPlayer:
      return t('canvasUI.canvasBuilder.components.descriptions.collectionPlayer')
    case ComponentTypes.ContentList:
      return t('canvasUI.canvasBuilder.components.descriptions.contentList')
    case ComponentTypes.Block:
      return options.description || t('canvasUI.canvasBuilder.components.descriptions.block')
    case ComponentTypes.Scribble:
      return t('canvasUI.canvasBuilder.components.descriptions.scribble')
    case ComponentTypes.DynamicDataTable:
      return t('canvasUI.canvasBuilder.components.descriptions.dynamicDataTable')
    case ComponentTypes.DataCharts:
      return t('canvasUI.canvasBuilder.components.descriptions.dataCharts')
    case ComponentTypes.CustomDataTable:
      return t('canvasUI.canvasBuilder.components.descriptions.customDataTable')
    default:
      return ''
  }
}

function getDefaultChildren(defaultChildren: ComponentMeta['defaultChildren']) {
  const { getComponentDefaultVisibility } = useCanvasVisibility()

  return defaultChildren.map((c): ComponentNode => {
    const type = c.type!
    const child = {
      type,
      visible: {
        edit_mode: getComponentDefaultVisibility(type, 'edit_mode'),
        presentation_mode: getComponentDefaultVisibility(type, 'presentation_mode'),
        pdf: getComponentDefaultVisibility(type, 'pdf'),
        web: getComponentDefaultVisibility(type, 'web'),
      },
      tracking_id: type,
      id: createNodeId(c.type),
      data: componentMeta[type].defaultData,
    } as ComponentNode

    if (c.children) child.children = c.children // TODO: can be removed if BE allows empty grid items

    return child
  })
}

export function getNodeDisplayNameByComponentType(node: ComponentNode | null | undefined): string {
  if (!node) return ''
  let item

  const { activeCanvas, isEditMode } = useCanvas()
  switch (node.type) {
    case ComponentTypes.Text:
      item = node as ComponentNode<TextProps>
      if (item.tracking_id !== item.type) return item.tracking_id
      if (isEditMode.value) return stripHtmlTags(item.data?.content) ?? ''
      return renderTemplate(stripHtmlTags(item.data?.content), activeCanvas.value?.context) ?? ''
    case ComponentTypes.ContentGrid:
      item = node as ComponentNode<ContentGridProps>
      return item.tracking_id !== item.type ? item.tracking_id : (item?.data?.items?.[0]?.file?.name ?? '')
    case ComponentTypes.Embeddable:
      item = node as ComponentNode<EmbeddableProps>
      return item.tracking_id !== item.type ? item.tracking_id : (item?.data?.file?.name ?? '')
    case ComponentTypes.Multimedia:
      item = node as ComponentNode<MultimediaProps>
      if (item?.data?.asset_type === 'external')
        return item.tracking_id !== item.type ? item.tracking_id : (item.data?.external_url?.split('/')?.pop() ?? '')
      return item.tracking_id !== item.type ? item.tracking_id : (item.data?.file?.name ?? '')
    case ComponentTypes.Carousel:
      item = node as ComponentNode<CarouselProps>
      return item.tracking_id !== item.type ? item.tracking_id : (item.data?.images?.[0]?.url?.split('/')?.pop() ?? '')
    case ComponentTypes.Html:
      item = node as ComponentNode<HtmlProps>
      if (item.tracking_id !== item.type) return item.tracking_id
      return stripHtmlTagsAndTrim(sanitizeHtml(item.data?.html)).substring(0, 15).trim() || item.tracking_id
    case ComponentTypes.IFrame:
      item = node as ComponentNode<IFrameProps>
      return item.tracking_id !== item.type ? item.tracking_id : ''
    case ComponentTypes.Grid:
      item = node as ComponentNode<GridProps>
      return item.tracking_id !== item.type
        ? item.tracking_id
        : (getNodeDisplayNameByComponentType(item?.children?.[0]) ?? '')
    case ComponentTypes.GridItem:
      item = node as ComponentNode<GridItemProps>
      return item.tracking_id !== item.type
        ? item.tracking_id
        : (getNodeDisplayNameByComponentType(item?.children?.[0]) ?? '')
    case ComponentTypes.SectionList:
      item = node as ComponentNode<SectionListProps>
      return item.tracking_id !== item.type ? item.tracking_id : ''
    case ComponentTypes.Link:
      item = node as ComponentNode<LinkProps>
      if (item.tracking_id !== item.type) return item.tracking_id
      if (isEditMode.value) return stripHtmlTags(item.data?.display_text ?? item.data?.url) ?? ''
      return renderTemplate(stripHtmlTags(item.data?.display_text ?? item.data?.url), activeCanvas.value?.context) ?? ''
    case ComponentTypes.CollectionPlayer:
      item = node as ComponentNode<CollectionPlayer>
      return (item.tracking_id !== item.type ? item.tracking_id : item.data?.name) ?? ''
    case ComponentTypes.Block:
      item = node as ComponentNode<BlockProps>
      return item.tracking_id !== item.type ? item.tracking_id : ''
    default:
      // 'Section' component type falls into that case
      return node.tracking_id ?? '' // tracking_id is required but due to external apps that might update the canvas with the old structure it escapes possible undefined
  }
}

export function getSectionGlobalComponentSpacing(section: CanvasRetrieve['sections'][number]) {
  return section.theme?.global_component_spacing >= 0 ? `${section.theme?.global_component_spacing}px` : undefined
}

function addAt(array: ComponentNode[], newComponent: ComponentNode, afterSiblingId: ComponentNode['id'] | null = null) {
  if (afterSiblingId) {
    const index = array.findIndex((n) => n.id === afterSiblingId) + 1
    index !== -1 ? array.splice(index, 0, newComponent) : array.push(newComponent)
  } else {
    array.push(newComponent)
  }
}

const containsComponentsKeys = (overrides: SectionOverrides) =>
  !!Object.keys(overrides).filter((key) => !['modified_by', 'modified_at'].includes(key)).length

export function discardSectionComponentOverride(
  sectionId: CanvasRetrieve['id'],
  componentId: ComponentNode['id'],
  overrides: SectionComponentOverrides,
): SectionComponentOverrides {
  let updatedOverrides = cloneDeep(overrides)
  const updatedSectionOverrides = updatedOverrides?.[sectionId]

  delete updatedSectionOverrides?.[componentId]
  if (updatedSectionOverrides && !containsComponentsKeys(updatedSectionOverrides)) delete updatedOverrides[sectionId]
  else
    updatedOverrides = {
      ...updatedOverrides,
      [sectionId]: updatedSectionOverrides,
    }

  return updatedOverrides
}

export function addCanvasComponent(
  currentContent: ComponentNode[],
  add: {
    type: ComponentTypes
    parentId?: ComponentNode['id'] | null
    afterSiblingId?: ComponentNode['id'] | null
    condition?: (node: ComponentNode[]) => boolean
    withData?: ComponentNode['data'] | null
    withChildren?: ComponentNode['children']
    withExtraNodeData?: Partial<ComponentNode>
  },
) {
  const { type, parentId, afterSiblingId, condition, withData, withChildren, withExtraNodeData } = add
  if (condition && !condition(currentContent)) return { newContent: currentContent, newComponentId: null }

  const newComponentId = createNodeId(type)
  const newContent = simpleDeepClone(currentContent)

  const newComponent = {
    ...simpleDeepClone(withExtraNodeData),
    type,
    id: newComponentId,
    tracking_id: type,
    data: withData ?? componentMeta[type].defaultData,
    children: withChildren?.length ? withChildren : getDefaultChildren(componentMeta[type].defaultChildren),
  }

  if (parentId) {
    const node = findNodeInTreeById(newContent, parentId)
    if (!node) {
      console.error(`Could not find parent node with id: ${parentId}`)
      return { newContent: currentContent, newComponentId: null }
    }
    node.children = node.children ?? []
    addAt(node.children, newComponent, afterSiblingId)
  } else {
    addAt(newContent, newComponent, afterSiblingId)
  }

  return { newContent, newComponentId }
}

export function getAllPages(canvasContent: ComponentNode[], currentEmbedLocation: EmbedLocation, isEditMode = false) {
  const pagesArray: ComponentNode[][] = []
  let currPageComponents: ComponentNode[] = []
  const appsWithoutPage: ComponentNode[] = []

  canvasContent.forEach((component) => {
    if ([ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type)) {
      if (!(component as ComponentNode<SectionListProps>).data?.sections?.length) return
      if (currPageComponents.length) {
        pagesArray.push(currPageComponents)
        currPageComponents = []
      }
      pagesArray.push([component])
    } else if (component.type === ComponentTypes.PageBreak) {
      if (currPageComponents.length) {
        // do not add empty pages
        pagesArray.push(currPageComponents)
        currPageComponents = []
      }
    } else if (component.type === ComponentTypes.Page) {
      if (currPageComponents.length) {
        pagesArray.push(currPageComponents)
        currPageComponents = []
      }
      // Add the page component as is for navigation purposes, CanvasContent.paginated will take care of the replacement
      if (component.children && component.children.length) {
        pagesArray.push([component])
      }
    } else {
      currPageComponents.push(component)
    }
  })

  if (currPageComponents.length) {
    const isNewPageNotRequired = currPageComponents.every((component) =>
      isHeadlessOrNotAvailableApp(component, isEditMode, currentEmbedLocation),
    )

    const availableComponents = currPageComponents.filter((component) => {
      const isEmbeddable = component.type === ComponentTypes.Embeddable

      if (isEmbeddable) {
        return (component as ComponentNode<EmbeddableProps>)?.data?.embed_locations?.includes(currentEmbedLocation)
      }

      return true
    })

    if (isNewPageNotRequired) {
      appsWithoutPage.push(...availableComponents)
    } else {
      pagesArray.push(availableComponents)
    }
  }

  if (appsWithoutPage.length) {
    if (pagesArray.length) {
      pagesArray[0].push(...appsWithoutPage)
    } else {
      pagesArray.push(appsWithoutPage)
    }
  }

  return pagesArray
}

export function getPageQuantity(canvasContent: ComponentNode[][]) {
  let total = 0

  canvasContent.forEach((page) => {
    const sectionLists = page.filter((component) => {
      return component.type === ComponentTypes.SectionList
    }) as ComponentNode<SectionListProps>[]

    if (sectionLists.length > 0) {
      sectionLists.forEach((component) => {
        total += component.data!.sections.length
      })
    } else {
      // Count PageBreak components as individual pages
      const pageBreaks = page.filter((component) => component.type === ComponentTypes.PageBreak)
      if (pageBreaks.length > 0) {
        total += pageBreaks.length
      } else {
        total += 1
      }
    }
  })

  return total
}

export const getPageRange = (
  canvasContent: ComponentNode[],
  currentEmbedLocation: EmbedLocation,
  start?: number,
  end?: number,
): ComponentNode[][] => {
  const pages = getAllPages(canvasContent, currentEmbedLocation)

  if (!start || !end) {
    return pages
  }

  let total = 0
  const outputPages: ComponentNode[][] = []

  for (const page of pages) {
    const pageResponse = getPage(page, total, start, end)

    total = pageResponse.total

    if (pageResponse.page) {
      outputPages.push(pageResponse.page)
    }

    if (total >= end) {
      break
    }
  }

  return outputPages
}

const getPage = (page: ComponentNode[], total: number, start: number, end: number) => {
  const sectionLists = page.filter((component) => {
    return component.type === ComponentTypes.SectionList
  }) as ComponentNode<SectionListProps>[]

  if (sectionLists.length === 0) {
    // Count PageBreak and Page components as individual pages
    const pageBreaks = page.filter(
      (component) => component.type === ComponentTypes.PageBreak || component.type === ComponentTypes.Page,
    )
    total += pageBreaks.length > 0 ? pageBreaks.length : 1

    return {
      total,
      page: total >= start ? page : null,
    }
  }

  const outputSectionLists: ComponentNode[] = []

  for (const sectionList of sectionLists) {
    const clonedSectionList = cloneDeep(sectionList)

    const sectionsQuantity = sectionList.data?.sections.length
    if (!sectionsQuantity) {
      continue
    }
    const neededSections = end - total

    const skipIndex = total < start ? start - 1 - total : 0
    let startIndex = 0
    let endIndex = 0

    total += sectionsQuantity

    if (total < start) {
      continue
    } else {
      startIndex = skipIndex
      endIndex = Math.min(neededSections, sectionsQuantity)
    }

    if (!clonedSectionList.data) {
      continue
    }
    clonedSectionList.data.sections = sectionList.data?.sections.slice(startIndex, endIndex) ?? []
    outputSectionLists.push(clonedSectionList)

    if (total >= end) {
      break
    }
  }

  return {
    total,
    page: outputSectionLists.length > 0 ? outputSectionLists : null,
  }
}

export function scrollCanvasToTop() {
  const scrollableElements = document.querySelectorAll('.canvas-builder-layout .n-layout-scroll-container')
  scrollableElements.forEach((element) => {
    element.scrollTop = 0
  })
}

export function shouldShowEmbeddable(
  isEditMode: boolean,
  currentEmbedLocation: EmbedLocation,
  headlessLocations?: string[],
) {
  if (isEditMode) return true
  return !headlessLocations?.includes?.(currentEmbedLocation)
}

export const isHeadlessOrNotAvailableApp = (
  component: ComponentNode,
  isEditMode: boolean,
  currentEmbedLocation: EmbedLocation,
) => {
  const isEmbeddable = component.type === ComponentTypes.Embeddable

  if (!isEmbeddable) {
    return false
  }

  const isHeadless = !shouldShowEmbeddable(
    isEditMode,
    currentEmbedLocation,
    (component as ComponentNode<EmbeddableProps>)?.data?.headless_locations,
  )

  const isNotAvailable = !(component as ComponentNode<EmbeddableProps>)?.data?.embed_locations?.includes(
    currentEmbedLocation,
  )

  return isHeadless || isNotAvailable
}

export function shouldDisplayPlaceholderComponent(
  isEditMode: boolean,
  mode: (typeof CanvasBuilderMode)[keyof typeof CanvasBuilderMode] | undefined,
  visibility?: ComponentNodeVisible,
) {
  return isEditMode && mode === CLIENT_TYPE.impact && visibility?.edit_mode === false
}

export function isTextComponentEmpty(
  component: ComponentNode,
  overrides?: SectionComponentOverrides,
  sectionId?: CanvasRetrieve['id'],
) {
  let overrideContent = ''
  if (sectionId && overrides?.[sectionId]?.[component.id]) {
    const node = overrides?.[sectionId]?.[component.id].node
    overrideContent = (node?.data as TextProps)?.content ?? ''
    return stripHtmlTagsAndTrim(overrideContent) === ''
  }

  const componentContent = (component.data as TextProps)?.content ?? ''
  return stripHtmlTagsAndTrim(componentContent) === ''
}

type FoundComponent = {
  sectionId?: CanvasRetrieve['id']
  sectionListId?: ComponentNode['id']
  componentId: ComponentNode['id']
} | null

export function findEmbeddableInSectionsContent(
  canvasPages: ComponentNode[][],
  sectionComponentNodesBySectionId: UnwrapRef<SectionComponentNodesBySectionId>,
  appName: string,
): FoundComponent {
  let found = null

  // This label is used to break out of nested loops
  outerLoop: for (const page of canvasPages) {
    for (const component of page) {
      if (component.type === 'SectionList') {
        const sectionList = component as ComponentNode<SectionListProps>
        for (const s of sectionList.data?.sections || []) {
          for (const c of Object.values(sectionComponentNodesBySectionId[s.id] || {})) {
            if (c.type !== 'Embeddable') continue
            const embeddableComponent = c as ComponentNode<EmbeddableProps>

            if (embeddableComponent.data?.app_name === appName) {
              found = {
                sectionId: s.id,
                sectionListId: sectionList.id,
                componentId: embeddableComponent.id,
              }
              break outerLoop
            }
          }
        }
      }
    }
  }

  return found
}

export function findAllEmbeddableTypesInCanvasContent(componentNodesById: UnwrapRef<ComponentNodesById>): Set<string> {
  const found = []
  for (const componentId in componentNodesById) {
    const component = componentNodesById[componentId]
    if (component.type === 'Embeddable') {
      const appName = (component as ComponentNode<EmbeddableProps>).data?.app_name
      if (appName) {
        found.push(appName)
      }
    }
  }

  return new Set(found)
}

export function findAllEmbeddableTypesInSectionsContent(
  sectionComponentNodesBySectionId: UnwrapRef<SectionComponentNodesBySectionId>,
): Set<string> {
  const found = []
  for (const sectionId in sectionComponentNodesBySectionId) {
    const nodesById = sectionComponentNodesBySectionId[sectionId]
    for (const componentId in nodesById) {
      const component = nodesById[componentId]
      if (component.type === 'Embeddable') {
        const appName = (component as ComponentNode<EmbeddableProps>).data?.app_name
        if (appName) {
          found.push(appName)
        }
      }
    }
  }

  return new Set(found)
}

export function findEmbeddableInCanvasContent(
  componentNodesById: UnwrapRef<ComponentNodesById>,
  appName: string,
): FoundComponent {
  for (const componentId in componentNodesById) {
    const component = componentNodesById[componentId]
    if (component.type === 'Embeddable' && (component as ComponentNode<EmbeddableProps>).data?.app_name === appName) {
      return { componentId }
    }
  }

  return null
}

export function scrollToComponentById(
  id: ComponentNode['id'] | null | undefined,
  sectionId?: string,
  sectionIndex?: number,
  instant = false,
  delay = 250,
) {
  if (!id) return

  setTimeout(() => {
    let query = `[data-component-id="${id}"]`

    if (sectionId !== undefined) {
      query += ` [data-section-id="${sectionId}"]`

      if (sectionIndex !== undefined) {
        query += `[data-section-index="${sectionIndex}"]`
      }
    }

    const element = document.querySelector(query)
    if (!element) {
      console.warn(`scrollToComponentById: element not found for id: ${id}`)
      return
    }

    element.scrollIntoView({ behavior: instant ? 'instant' : 'smooth' })
  }, delay)
}

export function updateFirstContentGridWithShareboxItems(
  canvasContent: ComponentNode[],
  selectedItems: (SelectedFile | SelectedPage)[],
  filesById: Record<FileRetrieve['id'], FileRetrieve>,
) {
  let content = simpleDeepClone(canvasContent)

  // First, try to find a ContentGrid marked as sharebox target
  let contentGridNode = findShareboxTargetContentGridNode(content)

  // If no sharebox target found, fall back to first empty ContentGrid
  if (!contentGridNode) {
    contentGridNode = findFirstEmptyContentGridNode(content)
  }

  // If still no ContentGrid found, create one
  if (!contentGridNode) {
    console.info('No ContentGrid component found in the canvas content. Creating one...')
    const { newContent } = addCanvasComponent(canvasContent, {
      type: ComponentTypes.ContentGrid,
    })
    content = newContent
    contentGridNode = findFirstEmptyContentGridNode(content)
  }

  if (!contentGridNode) {
    console.error('Could not find or create a ContentGrid component in the canvas content')
    return content
  }

  const data = prepareContentGridNodeData({ node: contentGridNode, filesById, selectedItems })
  contentGridNode.data = data

  return content
}

function findShareboxTargetContentGridNode(nodes: ComponentNode[]): ComponentNode<ContentGridProps> | null {
  for (const node of nodes) {
    if (node.type === ComponentTypes.ContentGrid && node.is_sharebox_target) {
      return node as ComponentNode<ContentGridProps>
    }
    if (node.children) {
      const found = findShareboxTargetContentGridNode(node.children)
      if (found) {
        return found
      }
    }
  }
  return null
}

function findFirstEmptyContentGridNode(nodes: ComponentNode[]): ComponentNode<ContentGridProps> | null {
  for (const node of nodes) {
    if (node.type === ComponentTypes.ContentGrid && !(node.data as ContentGridProps)?.items?.length) {
      return node as ComponentNode<ContentGridProps>
    }
    if (node.children) {
      const found = findFirstEmptyContentGridNode(node.children)
      if (found) {
        return found
      }
    }
  }
  return null
}

function findShareboxTargetCollectionPlayerNode(
  nodes: ComponentNode[],
): ComponentNode<CollectionPlayer | CollectionPlayerV2> | null {
  for (const node of nodes) {
    if (node.type === ComponentTypes.CollectionPlayer && node.is_sharebox_target) {
      return node as ComponentNode<CollectionPlayer | CollectionPlayerV2>
    }
    if (node.children) {
      const found = findShareboxTargetCollectionPlayerNode(node.children)
      if (found) {
        return found
      }
    }
  }
  return null
}

function findFirstEmptyCollectionPlayerNode(
  nodes: ComponentNode[],
): ComponentNode<CollectionPlayer | CollectionPlayerV2> | null {
  for (const node of nodes) {
    if (node.type === ComponentTypes.CollectionPlayer) {
      const data = node.data as CollectionPlayer | CollectionPlayerV2 | undefined
      const hasContent = data?.groups?.some((g) => g.slides?.length > 0)
      if (!hasContent) {
        return node as ComponentNode<CollectionPlayer | CollectionPlayerV2>
      }
    }
    if (node.children) {
      const found = findFirstEmptyCollectionPlayerNode(node.children)
      if (found) {
        return found
      }
    }
  }
  return null
}

export function updateFirstCollectionPlayerWithShareboxItems(
  canvasContent: ComponentNode[],
  selectedItems: (SelectedFile | SelectedPage)[],
  filesById: Record<FileRetrieve['id'], FileRetrieve>,
) {
  let content = simpleDeepClone(canvasContent)

  // First, try to find a CollectionPlayer marked as sharebox target
  let collectionPlayerNode = findShareboxTargetCollectionPlayerNode(content)

  // If no sharebox target found, fall back to first empty CollectionPlayer
  if (!collectionPlayerNode) {
    collectionPlayerNode = findFirstEmptyCollectionPlayerNode(content)
  }

  // If still no CollectionPlayer found, create one
  if (!collectionPlayerNode) {
    console.info('No CollectionPlayer component found in the canvas content. Creating one...')
    const { newContent } = addCanvasComponent(canvasContent, {
      type: ComponentTypes.CollectionPlayer,
    })
    content = newContent
    collectionPlayerNode = findFirstEmptyCollectionPlayerNode(content)
  }

  if (!collectionPlayerNode) {
    console.error('Could not find or create a CollectionPlayer component in the canvas content')
    return content
  }

  const data = prepareCollectionPlayerNodeData({ node: collectionPlayerNode, filesById, selectedItems })
  collectionPlayerNode.data = data

  return content
}

function prepareCollectionPlayerNodeData({
  selectedItems,
  filesById,
  node,
}: {
  selectedItems: (SelectedFile | SelectedPage)[]
  filesById: Record<FileRetrieve['id'], FileRetrieve>
  node?: ComponentNode<CollectionPlayer | CollectionPlayerV2>
}): CollectionPlayerV2 {
  // Use V2 format with file_id references for better lazy loading
  const slides = parseContentSelectorToCollectionPlayerSlides({ selectedItems, filesById })

  // Create a single group with all the slides
  const group: CollectionGroupV2 = {
    id: uuid(),
    name: 'Shared Content',
    slides: slides.map((slide) => ({
      id: uuid(),
      file_id: slide.file.id,
      index: slide.type === 'slide' ? slide.slide.index : undefined,
    })),
  }

  return {
    ...((node?.data as CollectionPlayerV2) ?? {}),
    name: 'Sharebox Collection',
    groups: [group],
  }
}

function isMultimediaDataWithTheme(obj: any, value: any): boolean {
  return obj && obj.type === 'Multimedia' && obj.theme_meta && typeof value === 'object' && value !== null
}

function isThemeRelatedComponent(obj: any, componentsWithThemeMeta = new Set<string>()): boolean {
  if (!obj) return false

  if (obj.id && componentsWithThemeMeta.has(obj.id)) {
    return true
  }

  if (obj.type === 'Multimedia' && obj.theme_meta) {
    if (obj.id) componentsWithThemeMeta.add(obj.id)
    return true
  }

  let current = obj
  let depth = 0
  const maxDepth = 3

  while (current && depth < maxDepth) {
    if (current.id && componentsWithThemeMeta.has(current.id)) {
      return true
    }

    if (current.type === 'Multimedia' && current.theme_meta) {
      if (current.id) componentsWithThemeMeta.add(current.id)
      return true
    }

    if (!current.parent) break
    current = current.parent
    depth++
  }

  return false
}

export function handleThemeAssetComparison(
  key: string | symbol | number,
  objValue: any,
  othValue: any,
  object: any,
  other: any,
  fileProps: string[],
  isEqual: (obj1: any, obj2: any) => boolean,
  componentsWithThemeMeta?: Set<string>,
): boolean | undefined {
  const { isManuallyChangedThemeAsset } = useCanvasThemeAssets()

  if (key === 'theme_meta') {
    return true
  }

  // Check if this component was manually changed - if so, don't ignore differences
  if (object?.id && isManuallyChangedThemeAsset(object.id)) {
    return false
  }
  if (other?.id && isManuallyChangedThemeAsset(other.id)) {
    return false
  }

  const isThemeMedia = isThemeRelatedComponent(object, componentsWithThemeMeta || new Set())
  const otherIsThemeMedia = isThemeRelatedComponent(other, componentsWithThemeMeta || new Set())

  if (key === 'data' && (isMultimediaDataWithTheme(object, objValue) || isMultimediaDataWithTheme(other, othValue))) {
    if (typeof objValue === 'object' && typeof othValue === 'object') {
      const objWithoutFile = cloneDeep(objValue)
      const othWithoutFile = cloneDeep(othValue)

      if (objWithoutFile && 'file' in objWithoutFile) {
        objWithoutFile.file = null
      }

      if (othWithoutFile && 'file' in othWithoutFile) {
        othWithoutFile.file = null
      }

      fileProps.forEach((prop) => {
        if (prop in objWithoutFile) objWithoutFile[prop] = null
        if (prop in othWithoutFile) othWithoutFile[prop] = null
      })

      if (!!objWithoutFile?.file !== !!othWithoutFile?.file) {
        if (!objWithoutFile?.file) objWithoutFile.file = {}
        if (!othWithoutFile?.file) othWithoutFile.file = {}
      }

      const dataEqual = isEqual(objWithoutFile, othWithoutFile)
      if (dataEqual) return true
    }
  }

  if (isThemeMedia || otherIsThemeMedia) {
    if (key === 'file' || fileProps.includes(key as string)) {
      return true
    }

    if (typeof objValue === 'object' && typeof othValue === 'object') {
      if (
        (objValue && (objValue.asset_type === 'pitcher_asset' || objValue.content_url)) ||
        (othValue && (othValue.asset_type === 'pitcher_asset' || othValue.content_url))
      ) {
        return true
      }
    }
  }

  return undefined
}

// Helper function to recursively remove section theme assets and apply canvas theme assets
export function applyCanvasThemeAssetsToNode(node: ComponentNode): ComponentNode {
  if (node.type === 'Multimedia' && node.theme_meta) {
    // Remove theme_meta to prevent section theme assets from being applied
    const { theme_meta, ...nodeWithoutThemeMeta } = node
    node = nodeWithoutThemeMeta as ComponentNode

    // Apply canvas theme assets instead of leaving empty
    const { activeTheme } = useCanvasTheme()
    if (activeTheme.value) {
      // Check if this was a logo component
      if ((theme_meta as any).is_theme_logo && activeTheme.value.logo) {
        node.data = {
          asset_type: 'pitcher_asset',
          file: {
            id: activeTheme.value.logo.id,
            name: activeTheme.value.logo.name || 'Canvas Logo',
            content_url: activeTheme.value.logo.url,
          },
          type: 'image',
          horizontal_alignment: (node.data as any)?.horizontal_alignment,
        } as any
      }
      // Check if this was a theme asset component
      else if ((theme_meta as any).theme_asset_index !== undefined && activeTheme.value.assets?.length) {
        const assetIndex = Math.min((theme_meta as any).theme_asset_index, activeTheme.value.assets.length - 1)
        if (assetIndex >= 0) {
          const asset = activeTheme.value.assets[assetIndex]
          node.data = {
            asset_type: 'pitcher_asset',
            file: {
              id: asset.id,
              name: asset.name || `Canvas Asset ${assetIndex + 1}`,
              content_url: asset.url,
            },
            type: 'image',
            horizontal_alignment: (node.data as any)?.horizontal_alignment,
          } as any
        } else {
          // Fallback to empty if no assets available
          node.data = { type: 'image' } as any
        }
      } else {
        // Fallback to empty if no matching canvas theme asset
        node.data = { type: 'image' } as any
      }
    } else {
      // No canvas theme available, clear the multimedia data
      node.data = { type: 'image' } as any
    }
  }

  // Process children recursively
  if (node.children?.length) {
    node.children = node.children.map((child) => applyCanvasThemeAssetsToNode(child))
  }

  return node
}

// Helper function to process canvas sections when theme assets should be overridden
export function processCanvasForSectionThemeOverride(
  canvas: CanvasRetrieve,
  maintainSectionTheme: boolean,
): CanvasRetrieve {
  if (!canvas.sections?.length) {
    return canvas
  }

  return {
    ...canvas,
    sections: canvas.sections.map((section) => {
      // Apply canvas theme assets if:
      // 1. maintain_section_theme is false (apply to all sections), OR
      // 2. maintain_section_theme is true but this section inherits from parent
      const shouldApplyCanvasTheme = !maintainSectionTheme || section.theme?.inherit_theme_from_parent

      if (shouldApplyCanvasTheme && section.content?.data?.length) {
        const processedSectionData = section.content.data.map((node: ComponentNode) =>
          applyCanvasThemeAssetsToNode(cloneDeep(node)),
        )

        return {
          ...section,
          content: {
            ...section.content,
            data: processedSectionData,
          },
        }
      }
      return section
    }),
  }
}

export function isEmbeddableWithZeroHeight(component: ComponentNode): boolean {
  if (component.type !== ComponentTypes.Embeddable) {
    return false
  }

  const height = (component.data as EmbeddableProps)?.height || component?.style?.height
  return height === 0 || height === '0' || height === '0px'
}
